export type VersionState = 'current' | 'stale' | 'blocked'

export interface VersionManifest {
  build: number
  sha: string | null
  builtAt: string | null
  minSupportedBuild: number
}

export function parseBuildNumber(raw: unknown): number | null {
  let n: number
  if (typeof raw === 'number') {
    n = raw
  } else if (typeof raw === 'string' && raw.trim() !== '') {
    n = Number(raw)
  } else {
    return null
  }
  if (!Number.isInteger(n) || n < 0) return null
  return n
}

export function parseManifest(raw: unknown): VersionManifest | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>

  const build = parseBuildNumber(obj.build)
  const minSupportedBuild = parseBuildNumber(obj.minSupportedBuild)
  if (build === null || minSupportedBuild === null) return null

  const sha = typeof obj.sha === 'string' ? obj.sha : null
  const builtAt = typeof obj.builtAt === 'string' ? obj.builtAt : null

  return { build, sha, builtAt, minSupportedBuild }
}

export function evaluate(
  myBuild: number | null,
  manifest: VersionManifest | null,
  prev: VersionState,
): VersionState {
  // Rule 1: an unknown local build can never be evaluated against a manifest -
  // never force a state change without knowing what we're running.
  if (myBuild === null) return prev

  // Rule 2: a failed/missing manifest fetch is not information - keep prev as-is.
  if (manifest === null) return prev

  // Rule 3: if the manifest itself is incoherent (build < minSupportedBuild),
  // it cannot be trusted - e.g. a CI counter reset. Do not let a bad manifest
  // lock out every user; just hold prev until a sane manifest arrives.
  if (manifest.build < manifest.minSupportedBuild) return prev

  // Rule 4: the manifest says my build is no longer supported - block, since it
  // was a coherent manifest (rule 3 already filtered out incoherent ones), and
  // rule 1 already filtered out unknown local builds (never forced blocked).
  if (manifest.minSupportedBuild > myBuild) return 'blocked'

  // Rule 5: once blocked, latch - never un-block from here. A transient failed
  // fetch (handled by rule 2) or a later manifest must not silently let a
  // blocked tab keep running; blocked is a one-way gate.
  if (prev === 'blocked') return 'blocked'

  // Rule 6: a newer build is available than what's running - tell the tab it's stale.
  if (manifest.build > myBuild) return 'stale'

  // Rule 7: nothing newer, not blocked - we're current.
  return 'current'
}
